'use client'

import { ReactNode } from 'react'

import { cn } from '@/shared/lib/utils'

// Lighter, flatter sibling to Tabs.tsx's Tab (3D-bevel button, built for 2-3
// centered tabs) — sized for a horizontally scrollable rail of 5+ chips with
// nothing hidden behind a "Mais" dropdown. See design.md Decision 2.
interface ChipRailProps {
  children: ReactNode
  className?: string
}

export function ChipRail({ children, className }: ChipRailProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex w-full min-w-0 gap-2 overflow-x-auto scroll-smooth',
        'snap-x snap-mandatory',
        // Setting overflow-x forces the browser to also clip overflow-y
        // (CSS spec: a non-visible overflow-x coerces overflow-y from
        // visible to auto) — without this padding, Chip's hover lift
        // (-2px) and seated/press translateY (+3px, plus its box-shadow
        // bleed) get clipped by the rail's own bottom edge.
        'py-1.5',
        '[&::-webkit-scrollbar]:hidden',
        className
      )}
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {children}
    </div>
  )
}

interface ChipProps {
  active?: boolean
  onClick?: () => void
  children: ReactNode
  className?: string
}

// Arcade mechanic (same language as Button.tsx's emphasis/toggled variants):
// solid fill + a single 3D "ledge" via bottom box-shadow, no top highlight.
// Hover lifts -2px and the ledge grows to compensate; press/active seats the
// control flush onto the ledge (translateY 3px, shadow collapses to 0).
// Active/selected chips reuse the exact "toggled" look: bg-primary, white
// text, seated with an inset shadow instead of a ledge.
const ARCADE_BASE =
  'inline-flex h-[34px] shrink-0 snap-start items-center justify-center whitespace-nowrap ' +
  'rounded-[11px] px-4 font-hl text-sm font-bold select-none ' +
  'transition-[transform,box-shadow] duration-[90ms] ease-[cubic-bezier(0.2,0.8,0.3,1)]'

const ARCADE_SECONDARY =
  'border-2 border-primary/22 bg-white text-primary-dark ' +
  'shadow-[0_3px_0_var(--brand-gray-light)] ' +
  'hover:-translate-y-[2px] hover:shadow-[0_5px_0_var(--brand-gray-light)] ' +
  'active:translate-y-[3px] active:shadow-none'

const ARCADE_TOGGLED =
  'translate-y-[3px] bg-primary text-white shadow-[inset_0_3px_6px_rgba(0,0,0,0.30)]'

export function Chip({ active, onClick, children, className }: ChipProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(ARCADE_BASE, active ? ARCADE_TOGGLED : ARCADE_SECONDARY, className)}
    >
      {children}
    </button>
  )
}
